import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QrCode, LogOut, Copy, Check } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const inputCls =
  "w-full h-12 px-4 rounded-xl border border-zinc-200 bg-white text-base placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 transition-shadow";

const STATUS_LABELS = { pending: "Pendiente", contacted: "Contactada", approved: "Aprobada", rejected: "Rechazada" };
const STATUS_CLS = {
  pending: "bg-amber-50 text-amber-700",
  contacted: "bg-sky-50 text-sky-700",
  approved: "bg-emerald-50 text-emerald-700",
  rejected: "bg-zinc-100 text-zinc-500",
};

const fmtDate = (d) => {
  try {
    return format(new Date(d), "d MMM yyyy · HH:mm", { locale: es });
  } catch {
    return d;
  }
};

export default function PlatformAdmin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("requests");
  const [requests, setRequests] = useState(null);
  const [companies, setCompanies] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ company_name: "", admin_name: "", admin_email: "" });
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [resending, setResending] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  const loadRequests = () =>
    api.get("/platform/requests").then(({ data }) => setRequests(data)).catch((e) => toast.error(fmtErr(e)));
  const loadCompanies = () =>
    api.get("/platform/companies").then(({ data }) => setCompanies(data)).catch((e) => toast.error(fmtErr(e)));

  useEffect(() => {
    loadRequests();
    loadCompanies();
  }, []);

  const changeStatus = async (id, status) => {
    setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
    try {
      await api.patch(`/platform/requests/${id}`, { status });
      toast.success("Estado actualizado");
    } catch (e) {
      toast.error(fmtErr(e));
      loadRequests();
    }
  };

  const createCompany = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError("");
    setCreated(null);
    try {
      const { data } = await api.post("/platform/companies", form);
      setCreated(data);
      setForm({ company_name: "", admin_name: "", admin_email: "" });
      loadCompanies();
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setCreating(false);
    }
  };

  const resendInvite = async (userId) => {
    setResending(userId);
    setError("");
    setCreated(null);
    try {
      const { data } = await api.post("/platform/invitations/resend", { user_id: userId });
      setCreated({ ...data, resend: true });
      loadCompanies();
    } catch (e) {
      setError(fmtErr(e));
    } finally {
      setResending(null);
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(created.activation_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const handleLogout = async () => {
    navigate("/", { replace: true });
    await logout();
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="platform-page">
      <header className="sticky top-0 z-40 glass-nav border-b border-zinc-200/60">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <span className="h-9 w-9 rounded-xl bg-zinc-900 text-white flex items-center justify-center">
              <QrCode size={18} />
            </span>
            <div>
              <p className="font-heading font-bold text-lg tracking-tight leading-tight">Asset Passport</p>
              <p className="text-[11px] text-zinc-400 leading-tight">Administración de plataforma</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/app")}
              data-testid="platform-back-app"
              className="text-sm font-medium text-zinc-500 hover:text-zinc-900 transition-colors"
            >
              Ir a la aplicación
            </button>
            <span className="hidden sm:block text-sm font-medium" data-testid="platform-user-name">{user?.name}</span>
            <button
              onClick={handleLogout}
              data-testid="platform-logout-button"
              title="Cerrar sesión"
              className="h-10 w-10 rounded-full border border-zinc-200 bg-white flex items-center justify-center text-zinc-500 hover:text-zinc-900 hover:border-zinc-300 transition-colors duration-200"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-10">
        <div className="inline-flex bg-zinc-100 rounded-full p-1 mb-8" data-testid="platform-tabs">
          <button
            onClick={() => setTab("requests")}
            data-testid="platform-tab-requests"
            className={`h-10 px-5 rounded-full text-sm font-medium transition-colors duration-200 ${
              tab === "requests" ? "bg-white shadow-sm text-zinc-950" : "text-zinc-500 hover:text-zinc-800"
            }`}
          >
            Solicitudes
          </button>
          <button
            onClick={() => setTab("companies")}
            data-testid="platform-tab-companies"
            className={`h-10 px-5 rounded-full text-sm font-medium transition-colors duration-200 ${
              tab === "companies" ? "bg-white shadow-sm text-zinc-950" : "text-zinc-500 hover:text-zinc-800"
            }`}
          >
            Empresas
          </button>
        </div>

        {tab === "requests" && (
          <section data-testid="platform-requests-section">
            {!requests ? (
              <div className="flex justify-center py-16">
                <div className="h-8 w-8 rounded-full border-2 border-zinc-300 border-t-zinc-900 animate-spin" />
              </div>
            ) : requests.length === 0 ? (
              <div className="bg-white rounded-2xl border border-zinc-200/80 p-12 text-center" data-testid="platform-requests-empty">
                <p className="text-zinc-500">Todavía no hay solicitudes de acceso al piloto.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="platform-requests-list">
                {requests.map((r) => (
                  <div
                    key={r.id}
                    data-testid={`platform-request-${r.id}`}
                    className="bg-white rounded-2xl border border-zinc-200/80 p-5 flex flex-col lg:flex-row lg:items-center gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-zinc-900">{r.full_name}</p>
                        <span className="text-zinc-300">·</span>
                        <p className="text-sm text-zinc-700">{r.company_name}</p>
                      </div>
                      <p className="text-sm text-zinc-500 mt-1">
                        {r.email}
                        {r.phone ? ` · ${r.phone}` : ""}
                      </p>
                      {r.message && <p className="text-sm text-zinc-500 mt-1.5 line-clamp-2">{r.message}</p>}
                      <p className="text-xs text-zinc-400 mt-1.5">{fmtDate(r.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_CLS[r.status] || STATUS_CLS.pending}`}>
                        {STATUS_LABELS[r.status] || r.status}
                      </span>
                      <select
                        value={r.status}
                        onChange={(e) => changeStatus(r.id, e.target.value)}
                        data-testid={`platform-status-select-${r.id}`}
                        className="h-10 px-3 rounded-xl border border-zinc-200 bg-white text-sm text-zinc-700 focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2"
                      >
                        {Object.entries(STATUS_LABELS).map(([v, l]) => (
                          <option key={v} value={v}>{l}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {tab === "companies" && (
          <section data-testid="platform-companies-section">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-zinc-500">
                {companies ? `${companies.length} organización(es)` : ""}
              </p>
              <button
                onClick={() => { setShowForm((s) => !s); setCreated(null); setError(""); }}
                data-testid="platform-create-company-button"
                className="inline-flex items-center px-5 py-2.5 rounded-full bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 active:scale-95 transition-[background-color,transform] duration-200"
              >
                Crear empresa
              </button>
            </div>

            {showForm && (
              <form
                onSubmit={createCompany}
                className="bg-white rounded-2xl border border-zinc-200/80 card-shadow p-6 sm:p-7 mb-8 space-y-4"
                data-testid="platform-company-form"
              >
                <p className="text-sm text-zinc-600 leading-relaxed">
                  Se creará la organización y se enviará al administrador una invitación de un solo
                  uso (caduca en 48 horas) para que elija su propia contraseña.
                </p>
                <div className="grid sm:grid-cols-3 gap-4">
                  <input
                    className={inputCls}
                    placeholder="Empresa *"
                    value={form.company_name}
                    onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))}
                    required
                    maxLength={160}
                    data-testid="platform-company-name-input"
                  />
                  <input
                    className={inputCls}
                    placeholder="Nombre del administrador *"
                    value={form.admin_name}
                    onChange={(e) => setForm((f) => ({ ...f, admin_name: e.target.value }))}
                    required
                    maxLength={120}
                    data-testid="platform-admin-name-input"
                  />
                  <input
                    className={inputCls}
                    type="email"
                    placeholder="Email del administrador *"
                    value={form.admin_email}
                    onChange={(e) => setForm((f) => ({ ...f, admin_email: e.target.value }))}
                    required
                    data-testid="platform-admin-email-input"
                  />
                </div>
                {error && (
                  <p className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-4 py-2.5" data-testid="platform-create-error">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={creating}
                  data-testid="platform-company-submit"
                  className="h-11 px-6 rounded-full bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 active:scale-95 transition-[background-color,transform] duration-200 disabled:opacity-50"
                >
                  {creating ? "Creando…" : "Crear empresa e invitar administrador"}
                </button>
              </form>
            )}

            {created && (
              <div className="bg-emerald-50/60 border border-emerald-200 rounded-2xl p-6 mb-8" data-testid="platform-invitation-result">
                <p className="font-medium text-emerald-900">
                  {created.resend ? "Invitación reenviada." : "Empresa creada e invitación generada."}
                </p>
                <p className="text-sm text-emerald-800 mt-1">
                  {created.email_sent
                    ? `Email de activación enviado a ${created.admin_email}.`
                    : `El email a ${created.admin_email} no pudo enviarse: entrega este enlace de forma segura.`}
                  {" "}El enlace es de un solo uso y caduca en {created.invitation_expires_hours} horas.
                </p>
                <div className="mt-4 flex items-center gap-2">
                  <code className="flex-1 min-w-0 truncate bg-white border border-emerald-200 rounded-xl px-4 py-2.5 text-xs text-zinc-700" data-testid="platform-activation-url">
                    {created.activation_url}
                  </code>
                  <button
                    onClick={copyUrl}
                    data-testid="platform-copy-url-button"
                    className="h-10 w-10 shrink-0 rounded-xl border border-emerald-200 bg-white flex items-center justify-center text-zinc-600 hover:text-zinc-900 transition-colors"
                    title="Copiar enlace"
                  >
                    {copied ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
                  </button>
                </div>
              </div>
            )}

            {!companies ? (
              <div className="flex justify-center py-16">
                <div className="h-8 w-8 rounded-full border-2 border-zinc-300 border-t-zinc-900 animate-spin" />
              </div>
            ) : (
              <div className="space-y-3" data-testid="platform-companies-list">
                {companies.map((c) => (
                  <div
                    key={c.id}
                    data-testid={`platform-company-${c.id}`}
                    className="bg-white rounded-2xl border border-zinc-200/80 p-5 flex flex-col sm:flex-row sm:items-center gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-zinc-900">{c.name}</p>
                      <div className="text-sm text-zinc-500 mt-0.5 space-y-1">
                        {(c.admins || []).length === 0 && "Sin administrador"}
                        {(c.admins || []).map((a) => (
                          <span key={a.id || a.email} className="flex items-center gap-2 flex-wrap">
                            <span>{a.email}</span>
                            {a.invite_pending && (
                              <>
                                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
                                  Invitación pendiente
                                </span>
                                <button
                                  onClick={() => resendInvite(a.id)}
                                  disabled={resending === a.id}
                                  data-testid={`platform-resend-invite-${a.id}`}
                                  className="text-xs font-medium text-zinc-900 underline underline-offset-2 hover:text-zinc-600 transition-colors disabled:opacity-50"
                                >
                                  {resending === a.id ? "Reenviando…" : "Reenviar invitación"}
                                </button>
                              </>
                            )}
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-zinc-400 mt-1">Creada: {fmtDate(c.created_at)}</p>
                    </div>
                    <div className="flex gap-5 text-sm text-zinc-600 shrink-0">
                      <span data-testid={`platform-company-users-${c.id}`}>{c.user_count} usuarios</span>
                      <span>{c.machine_count} máquinas</span>
                      <span>{c.location_count} ubicaciones</span>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <span
                        data-testid={`platform-terms-${c.id}`}
                        className={`text-xs font-medium px-2.5 py-1 rounded-full ${c.terms_accepted ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}
                      >
                        Términos: {c.terms_accepted ? "Aceptados" : "Pendientes"}
                      </span>
                      <span
                        data-testid={`platform-dpa-${c.id}`}
                        className={`text-xs font-medium px-2.5 py-1 rounded-full ${c.dpa_accepted ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}
                      >
                        DPA: {c.dpa_accepted ? "Aceptado" : "Pendiente"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
